import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import InfoTooltip from '@shared/components/InfoTooltip';
import { MetricsDimensionsCustomizePopup } from '@shared/components/MetricsDimensionsCustomize';
import { useAggregations, useCriticalEvents } from '@src/services/queries';
import {
  DEFAULT_DIMENSION_TABLE_AGGREGATIONS,
  FULL_STEP_SPECIAL_AGGREGATIONS,
} from '@shared/constants/analysis';
import { useDimensions } from '@src/services/queries';
import { getDefaultOrderBy } from '@src/shared/utils';
import * as store from '../store';
import appTracker from '@shared/utils/sensor/apptracker';

export interface CustomizePopupProps {
  excludeDimensions?: string[];
  excludeMetrics?: string[];
  mode?: 'add' | 'edit';
  defaultMetricsValue?: string[];
  defaultDimensionValue?: string;
  children?: React.ReactNode;
}

const OK_TEXT_MAP = {
  add: 'Add',
  edit: 'Apply',
};

const CustomizePopup = ({
  excludeDimensions = [],
  excludeMetrics = FULL_STEP_SPECIAL_AGGREGATIONS,
  mode = 'add',
  defaultDimensionValue,
  defaultMetricsValue = DEFAULT_DIMENSION_TABLE_AGGREGATIONS,
  children,
}: CustomizePopupProps) => {
  const navigate = useNavigate();
  const { data: criticalEvents } = useCriticalEvents();
  const aggregations = useAggregations().data?.aggregations;
  const { data: dimensions } = useDimensions();
  const currentDimensions = store.useDashboardDimensionTables().map((it) => it.dimension);
  const excludeDimensionsSet = React.useMemo(
    () =>
      new Set(
        [
          ...excludeDimensions,
          ...(mode === 'edit'
            ? currentDimensions.filter((it) => it !== defaultDimensionValue)
            : currentDimensions),
        ].filter(Boolean),
      ),
    [currentDimensions, defaultDimensionValue, excludeDimensions, mode],
  );
  const dimensionOptions = React.useMemo(
    () => dimensions?.dimensionOptions.filter((it) => !excludeDimensionsSet.has(it.value)),
    [excludeDimensionsSet, dimensions],
  );
  const metricOptions = React.useMemo(
    () =>
      aggregations
        ?.filter((it) => it.type === 'pattern' && !excludeMetrics.includes(it.id))
        .map((it) => ({
          label: (
            <>
              {it.name}
              <InfoTooltip title={it.description} />
            </>
          ),
          value: it.id,
        })),
    [aggregations, excludeMetrics],
  );
  const pattern = store.useDashboardPattern();
  const propagations = store.useDashboardPropagationSettings();
  const onChange = store.useOnChange();
  const formInstance = useAtomValue(store.formInstanceAtom);
  const stepOptions = React.useMemo(() => {
    return pattern.map((item, index) => ({
      value: `step_${index + 1}`,
      label: `${index + 1} ${criticalEvents?.criticalEventMap?.get(item)?.name ?? item}`,
    }));
  }, [pattern, criticalEvents?.criticalEventMap]);

  return (
    <MetricsDimensionsCustomizePopup
      customizeProps={{
        dimensionOptions,
        metricOptions,
        stepOptions,
        defaultDimensionValue,
        defaultMetricsValue,
        propagationsValue: propagations,
        okText: OK_TEXT_MAP[mode],
        onOk: (value) => {
          onChange((draft) => {
            const defaultOrderBy = getDefaultOrderBy(value.metrics);

            if (mode === 'add') {
              draft.propagation_config[value.dimension] = value.propagation;
              draft.dimension_analyses.unshift({
                dimension: value.dimension,
                aggregations: value.metrics,
                order_by: defaultOrderBy,
              });
              // tracking event
              appTracker({
                name: 'Funnel Analysis Dimension Table Added',
                data: {
                  dimension: value.dimension,
                  aggregations: value.metrics,
                  order_by: defaultOrderBy,
                },
              });
            }

            if (mode === 'edit') {
              const index = draft.dimension_analyses.findIndex(
                (it) => it.dimension === defaultDimensionValue,
              );

              if (index === -1) return;

              const prevOrderBy = draft.dimension_analyses[index].order_by;

              // Update form field value into settings form
              formInstance?.setFieldValue(
                ['propagation_config', value.dimension],
                value.propagation,
              );

              draft.propagation_config[value.dimension] = value.propagation;
              draft.dimension_analyses[index] = {
                dimension: value.dimension,
                aggregations: value.metrics,
              };

              if (prevOrderBy?.field && !value.metrics.includes(prevOrderBy.field)) {
                draft.dimension_analyses[index].order_by = defaultOrderBy;
              }
            }
          });
        },
        onDimensionCreate: () => {
          void navigate('.', {
            state: { setting: 'dimension' },
            replace: true,
          });
        },
        onDimensionEdit: (id) => {
          void navigate('.', {
            state: { setting: 'dimension', id },
            replace: true,
          });
        },
      }}
    >
      {children}
    </MetricsDimensionsCustomizePopup>
  );
};

export default React.memo(CustomizePopup);
